import { useState } from "react";
import { 
  X, ArrowRight, ArrowLeft, CheckCircle, Layers, Target, 
  TrendingUp, DollarSign, Clock, Info, Zap
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const IMSAnalyzer = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    current_certifications: [],
    planned_certifications: [],
    integration_status: "",
    pain_points: [],
    organization_size: ""
  });

  const certifications = [
    { id: "iso9001", name: "ISO 9001", desc: "Quality Management", category: "quality" },
    { id: "iso22000", name: "ISO 22000", desc: "Food Safety", category: "food_safety" },
    { id: "fssc22000", name: "FSSC 22000", desc: "Food Safety Certification", category: "food_safety" },
    { id: "haccp", name: "HACCP", desc: "Hazard Analysis", category: "food_safety" },
    { id: "iso14001", name: "ISO 14001", desc: "Environmental", category: "environmental" },
    { id: "iso45001", name: "ISO 45001", desc: "Health & Safety", category: "safety" },
    { id: "iso27001", name: "ISO 27001", desc: "Information Security", category: "security" },
    { id: "iso22301", name: "ISO 22301", desc: "Business Continuity", category: "continuity" },
    { id: "iso50001", name: "ISO 50001", desc: "Energy Management", category: "environmental" }
  ];

  const integrationStatuses = [
    { id: "none", name: "Not Integrated", desc: "Separate systems, separate documentation" },
    { id: "partial", name: "Partially Integrated", desc: "Some shared elements (e.g., document control)" },
    { id: "mostly", name: "Mostly Integrated", desc: "Common structure, some separate processes" },
    { id: "fully", name: "Fully Integrated", desc: "Single IMS covering all standards" }
  ];

  const painPoints = [
    { id: "duplicate_docs", name: "Duplicate documentation", impact: "high" },
    { id: "multiple_audits", name: "Multiple separate audits", impact: "high" },
    { id: "conflicting_procedures", name: "Conflicting procedures", impact: "medium" },
    { id: "resource_strain", name: "Resource strain (multiple systems)", impact: "high" },
    { id: "training_burden", name: "Excessive training requirements", impact: "medium" },
    { id: "reporting_complexity", name: "Complex reporting requirements", impact: "medium" },
    { id: "audit_fatigue", name: "Audit fatigue among staff", impact: "medium" },
    { id: "inconsistent_approach", name: "Inconsistent management approach", impact: "low" }
  ];

  const organizationSizes = [
    { id: "small", name: "Small (1-50 employees)" },
    { id: "medium", name: "Medium (51-250 employees)" },
    { id: "large", name: "Large (251+ employees)" }
  ];

  const toggleCertification = (field, certId) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(certId)
        ? prev[field].filter(c => c !== certId)
        : [...prev[field], certId]
    }));
  };

  const togglePainPoint = (pointId) => {
    setFormData(prev => ({
      ...prev,
      pain_points: prev.pain_points.includes(pointId)
        ? prev.pain_points.filter(p => p !== pointId)
        : [...prev.pain_points, pointId]
    }));
  };

  const analyzeIMS = () => {
    setIsLoading(true);
    
    setTimeout(async () => {
      const allCerts = [...new Set([...formData.current_certifications, ...formData.planned_certifications])];
      const certDetails = allCerts.map(id => certifications.find(c => c.id === id));
      
      // Calculate integration potential
      const categories = [...new Set(certDetails.map(c => c?.category))];
      const integrationPotential = allCerts.length >= 3 ? 'high' : 
                                   allCerts.length === 2 ? 'medium' : 'low';
      
      // Calculate synergies
      const synergies = [];
      
      // High Level Structure (Annex SL) alignment
      const annexSLStandards = ['iso9001', 'iso14001', 'iso45001', 'iso27001', 'iso22301', 'iso50001'];
      const annexSLCount = allCerts.filter(c => annexSLStandards.includes(c)).length;
      if (annexSLCount >= 2) {
        synergies.push({
          type: "Annex SL Alignment",
          standards: allCerts.filter(c => annexSLStandards.includes(c)),
          benefit: "Common high-level structure enables 40-60% documentation sharing",
          savings: "30-40% reduction in documentation effort"
        });
      }

      // Food safety synergies
      const foodSafetyStandards = ['iso22000', 'fssc22000', 'haccp'];
      const foodSafetyCount = allCerts.filter(c => foodSafetyStandards.includes(c)).length;
      if (foodSafetyCount >= 2) {
        synergies.push({
          type: "Food Safety Integration",
          standards: allCerts.filter(c => foodSafetyStandards.includes(c)),
          benefit: "HACCP plan serves as foundation for ISO 22000/FSSC 22000",
          savings: "Reduced duplicate hazard analyses"
        });
      }

      // Quality + Food Safety
      if (allCerts.includes('iso9001') && (allCerts.includes('iso22000') || allCerts.includes('fssc22000'))) {
        synergies.push({
          type: "Quality-Food Safety Integration",
          standards: ['iso9001', allCerts.includes('iso22000') ? 'iso22000' : 'fssc22000'],
          benefit: "Shared document control, internal audit, and management review processes",
          savings: "20-30% reduction in audit time"
        });
      }

      // EHS Integration
      if (allCerts.includes('iso14001') && allCerts.includes('iso45001')) {
        synergies.push({
          type: "EHS (Environment, Health & Safety)",
          standards: ['iso14001', 'iso45001'],
          benefit: "Combined risk assessments, shared compliance monitoring",
          savings: "Single EHS management system approach"
        });
      }

      // Calculate estimated savings
      const baseAuditDays = allCerts.length * 3;
      const integratedAuditDays = Math.ceil(baseAuditDays * 0.6);
      const auditDaysSaved = baseAuditDays - integratedAuditDays;
      
      const estimatedSavings = {
        audit_days_per_year: auditDaysSaved,
        documentation_reduction: annexSLCount >= 2 ? "35-45%" : "15-25%",
        estimated_cost_savings: formData.organization_size === 'large' ? "$15,000-30,000/year" :
                               formData.organization_size === 'medium' ? "$8,000-15,000/year" : "$3,000-8,000/year"
      };

      // Generate integration roadmap
      const roadmap = [];
      
      if (formData.integration_status === 'none') {
        roadmap.push({
          phase: 1,
          name: "Foundation",
          duration: "2-3 months",
          activities: [
            "Gap analysis across all systems",
            "Develop common document control procedure",
            "Create integrated manual framework"
          ]
        });
      }
      
      roadmap.push({
        phase: roadmap.length + 1,
        name: "Core Integration",
        duration: "3-6 months",
        activities: [
          "Integrate internal audit program",
          "Combine management review process",
          "Unify corrective action procedures"
        ]
      });
      
      roadmap.push({
        phase: roadmap.length + 1,
        name: "Full Integration",
        duration: "2-4 months",
        activities: [
          "Harmonize all documented information",
          "Conduct integrated internal audit",
          "Prepare for combined certification audit"
        ]
      });

      const analysisResult = {
        id: `ims-${Date.now()}`,
        current_certifications: formData.current_certifications,
        planned_certifications: formData.planned_certifications,
        total_standards: allCerts.length,
        integration_status: formData.integration_status,
        integration_potential: integrationPotential,
        synergies: synergies,
        estimated_savings: estimatedSavings,
        roadmap: roadmap,
        quick_wins: [
          "Implement common document control system immediately",
          "Schedule combined management reviews",
          "Create single internal audit schedule covering all standards",
          "Develop unified training matrix"
        ],
        next_steps: [
          "Conduct detailed gap analysis against integrated requirements",
          "Develop an IMS implementation project plan",
          "Consider certification body that offers combined audits",
          "Train key staff on integrated management system approach",
          "Contact our consultants for IMS implementation support"
        ]
      };

      // Save to session
      const sessionId = localStorage.getItem("chatbot_session_id");
      if (sessionId) {
        try {
          await axios.post(`${API}/tools/session/save`, {
            session_id: sessionId,
            tool_name: "ims_analyzer",
            result: analysisResult
          });
        } catch (e) {
          console.error("Failed to save session:", e);
        }
      }

      setResult(analysisResult);
      setIsLoading(false);
    }, 1500);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Current Certifications</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select all management system certifications you currently hold.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {certifications.map((cert) => (
                <button
                  key={cert.id}
                  onClick={() => toggleCertification('current_certifications', cert.id)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    formData.current_certifications.includes(cert.id) 
                      ? 'border-brand bg-brand/5' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      formData.current_certifications.includes(cert.id) 
                        ? 'bg-brand border-brand' 
                        : 'border-slate-300'
                    }`}>
                      {formData.current_certifications.includes(cert.id) && (
                        <CheckCircle className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{cert.name}</div>
                      <div className="text-xs text-muted-foreground">{cert.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-end pt-4">
              <Button 
                onClick={() => setStep(2)} 
                className="bg-brand hover:bg-brand-dark"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Planned Certifications</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select any certifications you&apos;re planning to pursue.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {certifications.filter(c => !formData.current_certifications.includes(c.id)).map((cert) => (
                <button
                  key={cert.id}
                  onClick={() => toggleCertification('planned_certifications', cert.id)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    formData.planned_certifications.includes(cert.id) 
                      ? 'border-brand bg-brand/5' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      formData.planned_certifications.includes(cert.id) 
                        ? 'bg-brand border-brand' 
                        : 'border-slate-300'
                    }`}>
                      {formData.planned_certifications.includes(cert.id) && (
                        <CheckCircle className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{cert.name}</div>
                      <div className="text-xs text-muted-foreground">{cert.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                className="bg-brand hover:bg-brand-dark"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Current Integration Status</h3>
              <p className="text-sm text-muted-foreground mb-4">
                How integrated are your current management systems?
              </p>
            </div>
            <div className="space-y-3">
              {integrationStatuses.map((status) => (
                <button
                  key={status.id}
                  onClick={() => setFormData({ ...formData, integration_status: status.id })}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    formData.integration_status === status.id 
                      ? 'border-brand bg-brand/5' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  <div className="font-medium">{status.name}</div>
                  <div className="text-sm text-muted-foreground">{status.desc}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={() => setStep(4)} 
                className="bg-brand hover:bg-brand-dark"
                disabled={!formData.integration_status}
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Pain Points & Organization Size</h3>
              <p className="text-sm text-muted-foreground mb-4">
                What challenges do you face with multiple management systems?
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Current Challenges</label>
                <div className="grid grid-cols-2 gap-2">
                  {painPoints.map((point) => (
                    <button
                      key={point.id}
                      onClick={() => togglePainPoint(point.id)}
                      className={`p-2 rounded-lg border text-xs text-left transition-all ${
                        formData.pain_points.includes(point.id) 
                          ? 'border-brand bg-brand text-white' 
                          : 'border-slate-200 hover:border-brand/50'
                      }`}
                    >
                      {point.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Organization Size</label>
                <div className="space-y-2">
                  {organizationSizes.map((size) => (
                    <button
                      key={size.id}
                      onClick={() => setFormData({ ...formData, organization_size: size.id })}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                        formData.organization_size === size.id 
                          ? 'border-brand bg-brand/5' 
                          : 'border-slate-200 hover:border-brand/50'
                      }`}
                    >
                      {size.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={analyzeIMS} 
                className="bg-brand hover:bg-brand-dark"
                disabled={isLoading || !formData.organization_size}
              >
                {isLoading ? "Analyzing..." : "Analyze IMS Opportunity"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (result) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary">IMS Opportunity Analysis</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Summary */}
        <div className="bg-brand/5 border border-brand/20 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-brand" />
              <span className="font-semibold">Integration Potential: </span>
              <Badge className={
                result.integration_potential === 'high' ? 'bg-green-100 text-green-700' :
                result.integration_potential === 'medium' ? 'bg-amber-100 text-amber-700' :
                'bg-slate-100 text-slate-700'
              }>
                {result.integration_potential.toUpperCase()}
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-brand">{result.total_standards}</p>
              <p className="text-xs text-muted-foreground">Standards</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{result.synergies.length}</p>
              <p className="text-xs text-muted-foreground">Synergies Found</p>
            </div>
            <div>
              <p className="text-lg font-bold text-amber-600">{result.estimated_savings.audit_days_per_year}</p>
              <p className="text-xs text-muted-foreground">Audit Days Saved/Year</p>
            </div>
          </div>
        </div>

        {/* Estimated Savings */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Estimated Savings with IMS
          </h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Documentation Reduction</p>
              <p className="font-bold text-green-700">{result.estimated_savings.documentation_reduction}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Audit Days Saved</p>
              <p className="font-bold text-green-700">{result.estimated_savings.audit_days_per_year} days/year</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Est. Cost Savings</p>
              <p className="font-bold text-green-700">{result.estimated_savings.estimated_cost_savings}</p>
            </div>
          </div>
        </div>

        {/* Synergies */}
        {result.synergies.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              Integration Synergies
            </h3>
            <div className="space-y-3">
              {result.synergies.map((synergy, index) => (
                <div key={index} className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{synergy.type}</span>
                    <div className="flex gap-1">
                      {synergy.standards.map(std => (
                        <Badge key={std} variant="outline" className="text-xs">
                          {std.toUpperCase()}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">{synergy.benefit}</p>
                  <p className="text-xs text-green-600 mt-1">💰 {synergy.savings}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Integration Roadmap */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Target className="h-5 w-5 text-brand" />
            Integration Roadmap
          </h3>
          <div className="space-y-4">
            {result.roadmap.map((phase, index) => (
              <div key={index} className="relative pl-8">
                <div className="absolute left-0 top-0 w-6 h-6 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">
                  {phase.phase}
                </div>
                {index < result.roadmap.length - 1 && (
                  <div className="absolute left-3 top-6 w-0.5 h-full bg-brand/20" />
                )}
                <div className="bg-white rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{phase.name}</span>
                    <Badge variant="outline" className="text-xs flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {phase.duration}
                    </Badge>
                  </div>
                  <ul className="space-y-1">
                    {phase.activities.map((activity, i) => (
                      <li key={i} className="text-xs text-slate-600 flex items-start gap-2">
                        <CheckCircle className="h-3 w-3 text-brand mt-0.5 flex-shrink-0" />
                        {activity}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Wins */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-600" />
            Quick Wins - Start Today
          </h3>
          <ul className="space-y-1">
            {result.quick_wins.map((win, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                {win}
              </li>
            ))}
          </ul>
        </div>

        {/* Disclaimer */}
        <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 mb-6">
          <p className="text-xs text-slate-600 flex items-start gap-2">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            Savings estimates are based on typical integration projects. Actual results depend on 
            organization size, complexity, and implementation approach.
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { setResult(null); setStep(1); }} className="flex-1">
            Start Over
          </Button>
          <Button onClick={onClose} className="flex-1 bg-brand hover:bg-brand-dark">
            Request IMS Consultation
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-primary">IMS Opportunity Analyzer</h2>
          <p className="text-sm text-muted-foreground">Step {step} of 4</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-slate-100 rounded-full h-2 mb-8">
        <div 
          className="bg-brand h-2 rounded-full transition-all duration-300"
          style={{ width: `${(step / 4) * 100}%` }}
        />
      </div>

      {renderStep()}
    </div>
  );
};

export default IMSAnalyzer;
